import type { SWRConfiguration } from 'swr';
import { isTerminalAuthError } from '@/lib/auth/session-expiry';

type OnErrorRetry = NonNullable<SWRConfiguration['onErrorRetry']>;

/**
 * SWR error-retry policy.
 *
 * Behaves like SWR's built-in policy (exponential backoff with jitter, capped
 * by `errorRetryCount`) with one addition: a terminal auth error (401/403) stops
 * retrying immediately. Without this, a palette webview that has lost its
 * session cookie hammers every authenticated endpoint with 401s in a tight retry
 * loop, freezing the UI (ENG-1242).
 *
 * Note: SWR's built-in policy also skips retries while the document is hidden,
 * relying on `revalidateOnFocus` to resume them on focus. This provider sets
 * `revalidateOnFocus: false`, so a skipped retry would never resume and the
 * resource could stay stuck in an error state indefinitely. We therefore do NOT
 * pause on hidden — only the terminal 401/403 bail stops retries.
 */
export const onErrorRetry: OnErrorRetry = (
  error,
  _key,
  config,
  revalidate,
  revalidateOpts,
) => {
  // A missing/expired session (401) or forbidden resource (403) will never
  // succeed on retry — bail out instead of entering the backoff loop.
  if (isTerminalAuthError(error)) {
    return;
  }

  const { errorRetryCount, errorRetryInterval } = config;
  const currentRetryCount = revalidateOpts.retryCount;

  if (errorRetryCount !== undefined && currentRetryCount > errorRetryCount) {
    return;
  }

  // Exponential backoff with jitter, mirroring SWR's default timing. Retries are
  // scheduled even while the tab is hidden — see the note above on why we don't
  // pause here.
  const timeout =
    ~~((Math.random() + 0.5) * (1 << Math.min(currentRetryCount, 8))) *
    (errorRetryInterval ?? 5000);

  setTimeout(revalidate, timeout, revalidateOpts);
};
